import React, { useState, useEffect } from "react";
import { PlanMeta } from "../services/planService";
import * as planService from "../services/planService";
import "./TaskEditor.css";

interface PlanSettingsProps {
  plan: PlanMeta;
  onSave: (updates: { title: string; editMode: string; editors: string[] }) => void;
  onDelete: () => void;
  onCancel: () => void;
}

const PlanSettings: React.FC<PlanSettingsProps> = ({ plan, onSave, onDelete, onCancel }) => {
  const [title, setTitle] = useState(plan.title || "");
  const [editMode, setEditMode] = useState(plan.editMode || "everyone");
  const [editorsText, setEditorsText] = useState((plan.editors || []).join("\n"));

  // Sharing
  const [shareCode, setShareCode] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharedEmails, setSharedEmails] = useState<string[]>([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onCancel]);

  function handleSave() {
    const editors = editorsText
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && e.includes("@"));
    onSave({ title: title.trim() || plan.title, editMode, editors });
  }

  async function handleGetShareCode() {
    setLoadingCode(true);
    try {
      const code = await planService.getShareCode(plan.id);
      setShareCode(code);
    } catch (err) {
      console.error(err);
    }
    setLoadingCode(false);
  }

  function handleCopy() {
    navigator.clipboard.writeText(shareCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleShareByEmail() {
    if (!shareEmail.trim() || !shareEmail.includes("@")) return;
    setSharing(true);
    try {
      await planService.shareWithEmail(plan.id, shareEmail.trim());
      setSharedEmails(prev => [...prev, shareEmail.trim()]);
      setShareEmail("");
    } catch (err) {
      console.error(err);
    }
    setSharing(false);
  }

  return (
    <div className="task-editor-overlay" onClick={onCancel}>
      <div className="task-editor" onClick={(e) => e.stopPropagation()}>
        <div className="task-editor-header">
          <h2>Plan Settings</h2>
          <button className="task-editor-close" onClick={onCancel}>&#x2715;</button>
        </div>

        <div className="task-editor-body">
          <div className="task-editor-field">
            <label>Plan Name</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Plan name"
            />
          </div>

          <div className="task-editor-field">
            <label>Who can edit?</label>
            <div className="task-editor-type-pills">
              <button
                className={`task-editor-pill ${editMode === "everyone" ? "active" : ""}`}
                onClick={() => setEditMode("everyone")}
              >
                Everyone with access
              </button>
              <button
                className={`task-editor-pill ${editMode === "restricted" ? "active" : ""}`}
                onClick={() => setEditMode("restricted")}
              >
                Only specific people
              </button>
            </div>
          </div>

          {editMode === "restricted" && (
            <div className="task-editor-field">
              <label>Editors (one email per line)</label>
              <textarea
                className="task-editor-textarea"
                value={editorsText}
                onChange={(e) => setEditorsText(e.target.value)}
                placeholder={"jane@company.com\nbob@company.com"}
                rows={4}
              />
              <span style={{ fontSize: 12, color: "#888" }}>
                The plan owner can always edit regardless of this setting.
              </span>
            </div>
          )}

          {/* Sharing section */}
          <div className="task-editor-field task-editor-section-divider">
            <label>Share Code</label>
            {shareCode ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="text" value={shareCode} readOnly style={{ flex: 1, fontFamily: "monospace", fontSize: 16, letterSpacing: 1 }} />
                <button
                  className="task-editor-btn task-editor-btn-save"
                  style={{ padding: "8px 16px" }}
                  onClick={handleCopy}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            ) : (
              <button
                className="task-editor-btn task-editor-btn-save"
                style={{ alignSelf: "flex-start" }}
                onClick={handleGetShareCode}
                disabled={loadingCode}
              >
                {loadingCode ? "Generating..." : "Generate Share Code"}
              </button>
            )}
            <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              Anyone with this code can add this plan to their list.
            </span>
          </div>

        </div>

        <div className="task-editor-footer">
          <div className="task-editor-footer-left">
            <button className="task-editor-btn task-editor-btn-delete" onClick={onDelete}>
              Delete Plan
            </button>
          </div>
          <div className="task-editor-footer-right">
            <button className="task-editor-btn task-editor-btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="task-editor-btn task-editor-btn-save" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSettings;
